import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, pluck, switchMap, mergeMap, take, filter } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

import { HelperService } from './helper.service';
import { DeliveryAsset, OfflineDelivery } from '../models';
import { API } from 'app/shared/constants/app-service-constants';
import { StorageService } from './offline-storage.service';
import { OfflineFileService } from './offline-file.service';
import * as fromOfflineActions from '../store/actions/offline.actions';
import { ModalOfflineComponent } from 'app/shared/components/modals/modal-offline/modal-offline.component';
import { OfflineStatusesService } from './offline-statuses.service';
import {
  selectOfflineDelivery,
  selectDownloadingDelivery,
  selectIsDownloading,
} from '../store/selectors/offline.selectors';
import { ModalsService } from 'app/shared/components/modals/modals.service';

@Injectable({
  providedIn: 'root',
})
export class OfflineService {
  private isModalShown = false;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private fileService: OfflineFileService,
    private statusesService: OfflineStatusesService,
    private modalsService: ModalsService,
    private store: Store<any>,
  ) {}

  setDeliveryStatus(
    deliveryKeyCode: string,
    status: 'downloaded' | 'downloading' | 'readyToDownload',
  ): void {
    this.storageService.setDeliveryStatus(deliveryKeyCode, status);
  }

  fetchDeliveryAudioAssets(deliveryKeyCode: string): Observable<any> {
    const url = `${API.DELIVERIES_ASSET}/${deliveryKeyCode}/asset-category/audio`;
    return this.http.get(url).pipe(
      pluck('assets'),
      map((assets: any) => {
        const modifiedAssets = assets
          .map((asset: { campaignTrack: DeliveryAsset }) => asset.campaignTrack)
          .map(this.filterStreamingType(deliveryKeyCode))
          .filter(this.filterEmptyTypesAsset)
          .filter(this.filterUniqueAsset());

        return modifiedAssets;
      }),
    );
  }

  getAudioUrl(assetUrl): Observable<string> {
    return this.http.get(assetUrl, { responseType: 'text' });
  }

  downloadAudioFile(asset: DeliveryAsset): Observable<any> {
    const url = asset.types[0].deviceDownloadUrl;

    return this.getAudioUrl(url).pipe(
      switchMap((audioUrl: string) => this.startDownloading(audioUrl)),
    );
  }

  dispatchAssetsDownload(deliveryKeyCode) {
    this.store
      .pipe(
        select(selectOfflineDelivery, { deliveryKeyCode }),
        take(1),
        pluck('offlineAudioAssets'),
        filter((assets: DeliveryAsset[]) => assets && !!assets.length),
      )
      .subscribe((offlineAudioAssets: DeliveryAsset[]) => {
        const asset = offlineAudioAssets.find(this.isAssetReadyToDownload);
        if (asset) {
          asset.offlineDownloadStatus = 'DOWNLOADING';
          this.store.dispatch(fromOfflineActions.downloadAudioAsset({ asset }));
        } else {
          this.store.dispatch(fromOfflineActions.cancelDownloading({ deliveryKeyCode }));
          this.dispatchNextDeliveryDownload(deliveryKeyCode);
        }
      });
  }

  dispatchNextDeliveryDownload(deliveryKeyCode: string) {
    this.store
      .pipe(
        select(selectIsDownloading),
        filter((isDownloading: boolean) => !isDownloading),
        mergeMap(() => this.store.pipe(select(selectDownloadingDelivery, { deliveryKeyCode }))),
        take(1),
      )
      .subscribe((delivery: OfflineDelivery) => {
        this.dispatchAssetsDownload(delivery.deliveryKeyCode);
      });
  }

  isAssetReadyToDownload(asset: DeliveryAsset): boolean {
    const status = asset.offlineDownloadStatus;
    return (
      status !== 'COMPLETED' &&
      status !== 'DOWNLOADING' &&
      status !== 'DAMAGED' &&
      status !== 'CANCELLED'
    );
  }

  async clearAllCache(): Promise<any> {
    const removeArray = [];

    removeArray.push(await this.fileService.clearAll());
    removeArray.push(await this.storageService.removeAllDeliveries());

    return Promise.all(removeArray);
  }

  removeDeliveryFromStorage(deliveryKeyCode: string) {
    return this.storageService
      .removeDelivery(deliveryKeyCode)
      .catch((err: any) => console.error('offline.service', 'removeDeliveryFromStorage', err));
  }

  deleteFolder(deliveryKeyCode): any {
    return this.fileService.deleteFolder(deliveryKeyCode);
  }

  downloadImageFile(url: string, deliveryKeyCode: string, field: string): Observable<any> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      mergeMap((file: Blob) => from(this.fileService.writeToImgFile(
        { file, deliveryKeyCode, field },
      ))),
    );
  }

  showOfflineModal() {
    if (!this.isModalShown) {
      this.isModalShown = true;
      this.modalsService
        .showModal({
          component: ModalOfflineComponent,
          cssClass: 'modal-offline',
          backdropDismiss: false,
          componentProps: {},
        })
        .then(() => {
          this.isModalShown = false;
        });
    }
  }

  async closeOfflineModal() {
    this.isModalShown = false;
    return await this.modalsService.closeModal();
  }

  getAssetKeyCode(url: string): string {
    return this.statusesService.getAssetKeyCode(url);
  }

  getAssetFormat(url: string): string {
    return this.statusesService.getAssetFormat(url);
  }

  getCompletedOfflineAsset(assetItem, cachedAudioUrl): DeliveryAsset {
    let url = '';
    const asset = HelperService.cloneDeliveryAsset(assetItem);

    asset.types =
      asset.types &&
      asset.types.map((typeTrack: any) => {
        url = typeTrack.url;
        typeTrack.url = cachedAudioUrl;
        return typeTrack;
      });
    asset.assetKeyCode = this.getAssetKeyCode(url);
    asset.format = this.getAssetFormat(url);
    asset.offlineDownloadStatus = 'COMPLETED';

    return asset;
  }

  private startDownloading(url): Observable<any> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-store',
    });
    return this.http.get(url, { headers, responseType: 'blob' });
  }

  private filterUniqueAsset() {
    const existHash = {};

    return (asset: DeliveryAsset) => {
      if (existHash[asset.campaignTrackId]) {
        return false;
      }
      existHash[asset.campaignTrackId] = true;
      return true;
    };
  }

  private filterStreamingType(deliveryKeyCode) {
    return (assetItem: DeliveryAsset) => {
      assetItem.types = assetItem.types.filter((trackType: any) => trackType.type === 'Streaming');
      assetItem.deliveryKeyCode = deliveryKeyCode;
      return assetItem;
    };
  }

  private filterEmptyTypesAsset(filterAsset: DeliveryAsset) {
    return !!filterAsset.types.length;
  }
}
